// ═══════════════════════════════════════════════════════
// DATA UTILITIES — estrutura vazia e conversão de snapshot
// ═══════════════════════════════════════════════════════
import { logError } from "./logger.js";

/** Estrutura de dados vazia (cliente sem snapshot). */
export const DATA_EMPTY = {
  perf: {
    retJan: 0,
    retCDIjan: 0,
    pctCDIjan: 0,
    ret12m: 0,
    retCDI12m: 0,
    pctCDI12m: 0,
    ret2025: 0,
    ret2024: 0,
    acumulado: 0,
    cdiAcum2025: 0,
    cdiAcumInicio: 0,
    ganhoJan: 0,
    ganho12m: 0,
    volat12m: 0,
  },
  allocation: [],
  monthly: [],
  cumulative: [],
  periods: [],
  assetsXP: { posfix: [], inflacao: [], prefixado: [], prev: [] },
  assetsBTG: [],
  assetsInter: [],
  positionsAvenue: null,
  brinsonJan: {
    rows: [],
    portfolioRet: 0,
    benchmarkRet: 0,
    activeRet: 0,
    selection: 0,
    allocation: 0,
    interaction: 0,
  },
  brinson12m: {
    rows: [],
    portfolioRet: 0,
    benchmarkRet: 0,
    activeRet: 0,
    selection: 0,
    allocation: 0,
    interaction: 0,
  },
  patrimonio_total: null,
  patrimonio_xp: null,
  patrimonio_btg: null,
  patrimonio_inter: null,
  patrimonio_avenue_usd: null,
};

// ── FIX #310: sanitizador profundo de dados ─────────────────────
// Garante que nenhum valor dentro de arrays de dados para recharts
// seja um objeto (causaria "Objects are not valid as React child").
// Percorre recursivamente arrays e objetos rasos, convertendo qualquer
// valor inesperado para primitiva.

function sanitizeValue(v) {
  if (v == null) return v;
  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") return v;
  if (Array.isArray(v)) return v.map(sanitizeValue);
  if (typeof v === "object") {
    // Objeto raso: sanitizar cada propriedade
    const out = {};
    for (const [k, val] of Object.entries(v)) {
      if (
        val == null ||
        typeof val === "string" ||
        typeof val === "number" ||
        typeof val === "boolean"
      ) {
        out[k] = val;
      } else if (Array.isArray(val)) {
        out[k] = val.map(sanitizeValue);
      } else if (typeof val === "object") {
        // Nested object inesperado — converter para string e logar
        logError("dataUtils", `Objeto aninhado nos dados do snapshot!`, { key: k });
        out[k] = String(val);
      } else {
        out[k] = val;
      }
    }
    return out;
  }
  return String(v);
}

/**
 * Sanitiza arrays de dados que serão consumidos pelo recharts.
 * Garante que cada item do array é um objeto raso com valores primitivos.
 */
function sanitizeArray(arr) {
  if (!Array.isArray(arr)) return [];
  return arr.map(sanitizeValue);
}

// ── Helpers internos ────────────────────────────────────────────

/**
 * Filtra posições de assets_inter removendo sub-totais e cabeçalhos
 * que o parser Interwin pode ter incluído (dados legados no Supabase).
 */
const SUB_TOTAL_RE = /sub\s*-?\s*total/i;
const HEADER_NAMES = new Set([
  "título público",
  "titulo publico",
  "pós-fixado",
  "pos-fixado",
  "pós - fixado",
  "pos - fixado",
  "multimercado",
  "renda variável",
  "renda variavel",
  "global",
  "pré-fixado",
  "pre-fixado",
  "pré - fixado",
  "pre - fixado",
  "renda fixa",
  "previdência",
  "previdencia",
  "liquidez",
]);
function filterSubTotals(assets) {
  if (!assets?.length) return assets;
  return assets.filter((a) => {
    const name = (a.ticker || a.nome || "").toLowerCase().trim();
    if (SUB_TOTAL_RE.test(name)) return false;
    if (HEADER_NAMES.has(name)) return false;
    if (/^20\d{2}\s*-?\s*/.test(name)) return false;
    return true;
  });
}

/**
 * Mapeia nomes de categorias vindos de diferentes brokers/parsers
 * para os IDs padronizados de ALLOC_CLASSES usados no simulador de Aportes.
 */
export const ALLOC_NAME_MAP = {
  // Interwin
  "renda variável": "rv",
  "renda variavel": "rv",
  "título público": "ipca",
  "titulo publico": "ipca",
  multimercado: "multi",
  global: "inter",
  "pós-fixado": "pos",
  "pos-fixado": "pos",
  "pós - fixado": "pos",
  "renda fixa": "pos",
  "pré-fixado": "pre",
  "pre-fixado": "pre",
  "pré - fixado": "pre",
  previdência: "prev",
  previdencia: "prev",
  // NR001 / seed / XP
  "pós fixado": "pos",
  inflação: "ipca",
  "inflação (ntn-b)": "ipca",
  "pré fixado": "pre",
  caixa: "reserva",
  "reserva de emergência": "reserva",
  // CarteiraPage classes
  "cdb cdi": "pos",
  "equity/etf (usd)": "inter",
  "crédito privado": "credpriv",
  "cred. privado": "credpriv",
  outros: "multi",
};

/** Normaliza nome de categoria para ID ALLOC_CLASSES. */
export function normalizeAllocName(name) {
  if (!name) return "multi";
  return ALLOC_NAME_MAP[name.toLowerCase().trim()] || "multi";
}

const MONTH_LABELS = {
  jan: "Jan",
  fev: "Fev",
  mar: "Mar",
  abr: "Abr",
  mai: "Mai",
  jun: "Jun",
  jul: "Jul",
  ago: "Ago",
  set: "Set",
  out: "Out",
  nov: "Nov",
  dez: "Dez",
};

/**
 * Normaliza um item do array `monthly` vindo do parser/Supabase para o
 * formato esperado pelos componentes (PerformancePage, MetricasPage).
 *
 * O parser Interwin grava: { month: "jan", year: 2025, pct: 1.56, pct_cdi: 134 }
 * Os componentes esperam: { month: "Jan/25", p: 1.56, cdi: ~1.16, pctCdi: 134 }
 *
 * Se o CDI individual não estiver disponível, ele é estimado a partir de
 * `p` e `pct_cdi` (% do CDI): cdi ≈ p / (pct_cdi / 100).
 */
function normalizeMonthlyEntry(m) {
  // Já está no formato novo (tem campo `p`)
  if (m.p != null && m.month && m.month.includes("/")) return m;

  const p = m.p ?? m.pct ?? null;
  const pctCdi = m.pctCdi ?? m.pct_cdi ?? null;

  // Estimar CDI individual: se pct_cdi = 134 e p = 1.56, cdi ≈ 1.56 / 1.34 ≈ 1.16
  let cdi = m.cdi ?? null;
  if (cdi == null && pctCdi != null && pctCdi > 0 && p != null) {
    cdi = +(p / (pctCdi / 100)).toFixed(4);
  }

  // Formatar label do mês: "jan" + 2025 → "Jan/25"
  const rawMonth = (m.month || "").toLowerCase().trim();
  const label = MONTH_LABELS[rawMonth] || m.month || "?";
  const yr = m.year ? String(m.year).slice(-2) : "";
  const monthLabel = yr ? `${label}/${yr}` : label;

  return {
    month: monthLabel,
    p: p != null ? +Number(p).toFixed(4) : 0,
    cdi: cdi != null ? +Number(cdi).toFixed(4) : 0,
    pctCdi: pctCdi != null ? +Number(pctCdi).toFixed(2) : 0,
  };
}

/**
 * Computa o array de retornos acumulados a partir dos retornos mensais.
 * Retorna array de { m, p, cdi } onde cada posição é o acumulado até aquele mês.
 * O primeiro item (índice 0) representa o "Início" com p=0, cdi=0.
 * NOTA: inclui `m` (label do mês) para uso direto como dataKey no XAxis do recharts.
 */
function computeCumulative(monthly) {
  if (!monthly?.length) return [];
  const cum = [{ m: "Início", p: 0, cdi: 0 }];
  let cumP = 1;
  let cumCdi = 1;
  for (const m of monthly) {
    cumP *= 1 + (m.p || 0) / 100;
    cumCdi *= 1 + (m.cdi || 0) / 100;
    cum.push({
      m: m.month || "",
      p: +((cumP - 1) * 100).toFixed(4),
      cdi: +((cumCdi - 1) * 100).toFixed(4),
    });
  }
  return cum;
}

/**
 * Normaliza um item do array `periods` vindo do parser/Supabase para o
 * formato esperado pelos componentes (DashboardPage, MetricasPage).
 *
 * Parser Interwin grava: { label, portfolio, pct_cdi, cdi, volat }
 * Componentes esperam: { label, p, pctCdi, cdi, volat }
 */
function normalizePeriodEntry(entry) {
  if (entry.p != null && entry.pctCdi != null) return entry;
  return {
    label: entry.label || "",
    p: entry.p ?? entry.portfolio ?? 0,
    cdi: entry.cdi ?? 0,
    pctCdi: entry.pctCdi ?? entry.pct_cdi ?? 0,
    volat: entry.volat ?? 0,
  };
}

/** Cores padrão para alocação caso não venham preenchidas */
const ALLOC_COLORS = [
  "#C9A227",
  "#2563EB",
  "#7C3AED",
  "#16A34A",
  "#EA580C",
  "#DC2626",
  "#0891B2",
  "#4F46E5",
  "#059669",
  "#D97706",
];

/**
 * Normaliza um item do array `allocation` vindo do parser/Supabase para o
 * formato esperado pelos componentes.
 *
 * Parser grava: { name, pct }
 * Componentes esperam: { name, value, color, amount }
 */
function _normalizeAllocEntry(entry, idx, totalPatrimonio) {
  const value = entry.value ?? entry.pct ?? 0;
  const color = entry.color || ALLOC_COLORS[idx % ALLOC_COLORS.length];
  const amount = entry.amount ?? (totalPatrimonio > 0 ? (value / 100) * totalPatrimonio : 0);
  return {
    name: entry.name || "Outro",
    value: +Number(value).toFixed(2),
    color,
    amount: +Number(amount).toFixed(2),
  };
}

/**
 * Normaliza o array de alocação para que os percentuais somem exatamente
 * 100%. Recalcula amounts proporcionalmente ao patrimônio total.
 *
 * O parser Interwin extrai percentuais via regex do PDF, que frequentemente
 * não fecham em 100% (arredondamento, categorias faltantes, etc.).
 */
function normalizeAllocation(rawEntries, totalPatrimonio) {
  if (!rawEntries || rawEntries.length === 0) return [];

  // Primeiro passo: extrair valores brutos
  const entries = rawEntries.map((a, i) => ({
    name: a.name || "Outro",
    rawValue: a.value ?? a.pct ?? 0,
    color: a.color || ALLOC_COLORS[i % ALLOC_COLORS.length],
  }));

  // Somar percentuais brutos
  const rawSum = entries.reduce((s, e) => s + e.rawValue, 0);

  // Se a soma é zero, não há nada a normalizar
  if (rawSum <= 0) return [];

  // Normalizar para que somem exatamente 100%
  const scale = 100 / rawSum;
  const normalized = entries.map((e) => {
    const value = +(e.rawValue * scale).toFixed(2);
    const amount = totalPatrimonio > 0 ? +((value / 100) * totalPatrimonio).toFixed(2) : 0;
    return { name: e.name, value, color: e.color, amount };
  });

  // Ajuste de arredondamento: corrigir diferença residual no maior item
  const finalSum = normalized.reduce((s, e) => s + e.value, 0);
  const diff = +(100 - finalSum).toFixed(2);
  if (diff !== 0 && normalized.length > 0) {
    // Aplicar ajuste ao maior item (menor impacto visual percentual)
    const maxIdx = normalized.reduce((mi, e, i, arr) => (e.value > arr[mi].value ? i : mi), 0);
    normalized[maxIdx].value = +(normalized[maxIdx].value + diff).toFixed(2);
    // Recalcular amount do item ajustado
    if (totalPatrimonio > 0) {
      normalized[maxIdx].amount = +((normalized[maxIdx].value / 100) * totalPatrimonio).toFixed(2);
    }
  }

  return normalized;
}

/**
 * Converte um registro raw de `snapshots` (Supabase) para o formato
 * interno usado pelos componentes de visualização.
 *
 * @param {object} snap - Linha da tabela `snapshots`
 * @returns {object} Dados formatados para consumo nos componentes
 */
export function snapToData(snap) {
  // ── Normalizar monthly: converter formato Interwin → formato componente ──
  const rawMonthly = snap.monthly || [];
  const monthly = rawMonthly.map(normalizeMonthlyEntry);

  // ── Computar cumulative se ausente ou vazio ──
  const rawCumulative = snap.cumulative || [];
  const cumulative = rawCumulative.length > 0 ? rawCumulative : computeCumulative(monthly);

  // ── Derivar ret_month e ret_cdi_month do último mês quando ausentes ──
  const lastMonth = monthly.length > 0 ? monthly[monthly.length - 1] : null;
  const retMonth = snap.ret_month ?? (lastMonth?.p || 0);
  const retCdiMonth = snap.ret_cdi_month ?? (lastMonth?.cdi || 0);
  const pctCdiMonth = snap.pct_cdi_month ?? (lastMonth?.pctCdi || 0);

  // ── Derivar ret_12m dos últimos 12 meses quando ausente ──
  let ret12m = snap.ret_12m ?? 0;
  let retCdi12m = snap.ret_cdi_12m ?? 0;
  if (!snap.ret_12m && monthly.length >= 12) {
    const last12 = monthly.slice(-12);
    ret12m = +((last12.reduce((a, m) => a * (1 + (m.p || 0) / 100), 1) - 1) * 100).toFixed(2);
  }
  if (!snap.ret_cdi_12m && monthly.length >= 12) {
    const last12 = monthly.slice(-12);
    retCdi12m = +((last12.reduce((a, m) => a * (1 + (m.cdi || 0) / 100), 1) - 1) * 100).toFixed(2);
  }
  const pctCdi12m =
    snap.pct_cdi_12m ?? (retCdi12m > 0 ? +((ret12m / retCdi12m) * 100).toFixed(0) : 0);

  // ── CDI acumulado para períodos "2025" e "Início" (proxy SELIC) ──
  // Calcula a partir dos CDI mensais já presentes em monthly[],
  // eliminando os valores hardcoded anteriores (14.49 / 16.57).
  const cdiAcum2025 = (() => {
    const m2025 = monthly.filter((m) => {
      const yr = (m.month || "").split("/")[1];
      return yr === "25" || yr === "2025";
    });
    if (!m2025.length) return 0;
    return +((m2025.reduce((a, m) => a * (1 + (m.cdi || 0) / 100), 1) - 1) * 100).toFixed(2);
  })();
  const cdiAcumInicio = (() => {
    if (!monthly.length) return 0;
    return +((monthly.reduce((a, m) => a * (1 + (m.cdi || 0) / 100), 1) - 1) * 100).toFixed(2);
  })();

  // ── Normalizar periods: converter formato Interwin → formato componente ──
  const rawPeriods = snap.periods || [];
  const periods = rawPeriods.map(normalizePeriodEntry);

  // ── Normalizar allocation: converter pct → value, adicionar cores ──
  // Garante que os percentuais somem exatamente 100% (o parser pode extrair
  // valores que não fecham por arredondamento ou categorias faltantes).
  const rawAllocation = snap.allocation || [];
  const patTotal = Number(snap.patrimonio_total) || 0;
  const allocation = normalizeAllocation(rawAllocation, patTotal);

  return {
    perf: {
      retJan: retMonth,
      retCDIjan: retCdiMonth,
      pctCDIjan: pctCdiMonth,
      ret12m,
      retCDI12m: retCdi12m,
      pctCDI12m: pctCdi12m,
      ret2025: snap.ret_2025 ?? 0,
      ret2024: snap.ret_2024 ?? 0,
      acumulado: snap.acumulado ?? 0,
      cdiAcum2025,
      cdiAcumInicio,
      ganhoJan: snap.ganho_month ?? 0,
      ganho12m: snap.ganho_12m ?? 0,
      volat12m: snap.volat_12m ?? 0,
    },
    allocation: sanitizeArray(allocation),
    monthly: sanitizeArray(monthly),
    cumulative: sanitizeArray(cumulative),
    periods: sanitizeArray(periods),
    assetsXP: sanitizeValue(
      snap.assets_xp || { posfix: [], inflacao: [], prefixado: [], prev: [] }
    ),
    assetsBTG: sanitizeArray(snap.assets_btg || []),
    assetsInter: sanitizeArray(filterSubTotals(snap.assets_inter || [])),
    positionsAvenue: snap.positions_avenue ? sanitizeValue(snap.positions_avenue) : null,
    brinsonJan: sanitizeValue(
      snap.brinson_jan || {
        rows: [],
        portfolioRet: 0,
        benchmarkRet: 0,
        activeRet: 0,
        selection: 0,
        allocation: 0,
        interaction: 0,
      }
    ),
    brinson12m: sanitizeValue(
      snap.brinson_12m || {
        rows: [],
        portfolioRet: 0,
        benchmarkRet: 0,
        activeRet: 0,
        selection: 0,
        allocation: 0,
        interaction: 0,
      }
    ),
    // Posição internacional Avenue (USD)
    avenue: snap.avenue_position ? sanitizeValue(snap.avenue_position) : null,
    // Campos brutos de patrimônio (usados em ClientLayout para montar client.patrimonio)
    // NOTA: Supabase numeric → string; forçamos Number() para evitar tipos mistos.
    patrimonio_total: Number(snap.patrimonio_total) || 0,
    patrimonio_xp: Number(snap.patrimonio_xp) || 0,
    patrimonio_btg: Number(snap.patrimonio_btg) || 0,
    patrimonio_inter: Number(snap.patrimonio_inter) || 0,
    patrimonio_avenue_usd: Number(snap.patrimonio_avenue_usd) || 0,
    // Metadados do snapshot (para exibição dinâmica de datas)
    ref_date:
      typeof snap.ref_date === "string"
        ? snap.ref_date
        : snap.ref_date != null
          ? String(snap.ref_date)
          : null,
    period:
      typeof snap.period === "string"
        ? snap.period
        : snap.period != null
          ? String(snap.period)
          : null,
  };
}
